/**
 * Hook for reading guide texts
 * Provides access to reading guide texts with token replacement
 */

import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentEntity } from '../../store/slices/navigationSlice';
import { formatDomain } from '../../utils/entityHelpers';
import useTokenReplacement from './useTokenReplacement';
import { readingGuide } from '../../config/texts';

/**
 * Hook for accessing reading guide texts with token replacement
 * @param {Object} options - Hook options
 * @param {string} options.entityType - Type of entity for token replacement
 * @returns {Object} Reading guide texts access object
 */
const useReadingGuide = ({ entityType = 'commune' } = {}) => {
  const entity = useSelector(selectCurrentEntity);
  const domainKey = formatDomain(entity?.domaine);
  const replaceTokens = useTokenReplacement({ entityType });

  /**
   * Get text for a specific section with token replacement
   * @param {string} section - Section name (pourquoi, pourquoi-print, comprendre, saisons)
   * @returns {string} Processed text with tokens replaced
   */
  const getText = useCallback((section) => {
    const sectionData = readingGuide[section];
    if (!sectionData) {
      return '';
    }

    // Try domain-specific variant first, fallback to default
    const text = sectionData[domainKey] || sectionData.default || '';
    return replaceTokens(text);
  }, [domainKey, replaceTokens]);

  return {
    getText,
  };
};

export default useReadingGuide;
